import { useState } from 'react'

import { IconFlag } from '@tabler/icons-react'

import { CreateDefectModal } from '../../defects/CreateDefectModal'
import { BlockBase, Defect } from '../types/blueprint'

import { CaptureBlockActionButton } from './CaptureBlock.style'
import { getDefectsId } from './utils/mapValuesToTemplate'

import { Form } from '@/components/shared-components/BUI/Form'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { useEnabledModules } from '@/hooks/useEnabledModules'

import { BuildPassModule, CreateDefectUiMutation } from 'types/api.graphql'

export const ALLOW_ORPHANED_ACTIONS = 'ALLOW_ORPHANED_ACTIONS' // I THINK THIS COULD BE ENABLED FOR ALL TEMPLATES NOW BUT WE DID THIS IN A HURRY AND ONLY ENABLED IT FOR TOOLBOXES. BUT I'M PRETTY CONFIDENT THAT WE CAN CREATE PLATFORM ACTIONS WITHOUT A TEMPLATE RESPONSE ID BECAUSE THE API IS NOW HOOKING UP ORPHANED ACTIONS ON TEMPLATE RESPONSE SAVE

type InlineProps = {
  blockId: string
  value?: Defect[]
  onChange?: (defects: Defect[]) => void
}

const CreateDefectInline: React.FC<InlineProps> = ({
  blockId,
  value,
  onChange,
}) => {
  const { templateResponseId, extraInfo, renderOptions } = useTemplateRenderer()

  const [editing, setEditing] = useState(false)

  // only show actions on templates that have been created unless explicitly allowed
  if (
    !extraInfo[ALLOW_ORPHANED_ACTIONS] &&
    !renderOptions.isPreview &&
    !templateResponseId
  ) {
    return null
  }

  const handleDefectCreated = (
    defect: CreateDefectUiMutation['createDefect']
  ) => {
    onChange?.([
      ...(value ?? []),
      {
        id: defect.id,
        status: defect.status,
        title: defect.title,
        description: defect.description,
        attachments: defect.attachments?.map((a) => ({
          id: a.id,
          url: a.url,
        })),
      },
    ])
  }

  const startEditing = () => setEditing(true)
  const stopEditing = () => setEditing(false)

  return (
    <>
      <CaptureBlockActionButton
        icon={<IconFlag size={12} />}
        onClick={startEditing}
      >
        Defect
      </CaptureBlockActionButton>

      <CreateDefectModal
        createType={editing ? 'BUILDER_DEFECT' : undefined}
        closeModal={stopEditing}
        templateResponseId={templateResponseId}
        templateResponseBlockIdentifier={blockId}
        onCreate={handleDefectCreated}
        previewMode={!templateResponseId || !blockId}
      />
    </>
  )
}

type Props = {
  parentId: string
  defects: BlockBase['defects']
}

export const CaptureDefects: React.FC<Props> = ({ parentId, defects }) => {
  const { isModuleEnabled } = useEnabledModules()
  const defectsEnabled = isModuleEnabled(BuildPassModule.Defects)

  if (defects === undefined || defects === false || !defectsEnabled) {
    return null
  }

  const defectId = getDefectsId(parentId)
  const initialValue = Array.isArray(defects) ? defects : []

  return (
    <Form.Item name={defectId} initialValue={initialValue} noStyle>
      <CreateDefectInline blockId={parentId} />
    </Form.Item>
  )
}
